import { Button } from '@/components/shared/button/Button';
import { TimeshiftAvatar } from '@/components/shared/TimeshiftAvatar';
import { secondsToDisplayHours } from '@/helpers/dateHelper';
import { merge } from '@/helpers/Utility';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import { SeverityType, severityTypeToColor } from '@/services/warnings/warningDefinitions';
import { EmployeeForShiftItem } from '@/types/employeeForShift.types';
import { FC, SyntheticEvent } from 'react';
import { flushSync } from 'react-dom';
import { FiAlertTriangle, FiCalendar, FiClock } from 'react-icons/fi';
import { MdOutlinePerson } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import strings from '../roster.translations';

interface Props {
  option: EmployeeForShiftItem;
  onSelect: () => void;
  selected: boolean;
  onNavigating: (navigating: boolean) => void;
  onClose: () => void;
}

const RosterIssue: FC<{ issueText: string; major?: boolean }> = ({ issueText, major }) => {
  return (
    <div className="flex gap-2 text-sm overflow-wrap">
      <FiAlertTriangle
        className={'h-4 w-4 mt-0.5'}
        color={major ? severityTypeToColor(SeverityType.CRITICAL) : severityTypeToColor(SeverityType.WARNING)}
      />
      <div className="flex-1 text-left leading-tight">{issueText}</div>
    </div>
  );
};

export const RosterPickerOption: FC<Props> = ({ option, onSelect, selected, onNavigating, onClose }) => {
  const navigate = useNavigate();

  const { requestConfirmation, confirmationNeeded } = useConfirm(state => ({
    requestConfirmation: state.requestConfirmation,
    confirmationNeeded: state.confirmationNeeded
  }));

  const onTeamMemberNavigate = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    const identityId = option.details?.identityId;
    if (!identityId) throw new Error(`Employee ${option.name} missing identity id`);

    const destination = `/team-member/${identityId}`;

    const navigateAway = () => {
      onNavigating(false);
      onClose();
      // We need to put this in a timeout to make sure the page renders the dropdown closing.
      // Otherwise, the dropdown stays on top of the loading page for the destination.
      setTimeout(() => navigate(destination), 0);
    };

    if (confirmationNeeded) {
      flushSync(() => onNavigating(true));
      requestConfirmation(navigateAway);
    } else {
      navigateAway();
    }
  };

  return (
    <div
      className={merge(
        `border-b flex gap-3 p-2 items-center`,
        selected ? 'bg-primary-200' : !option.assignable ? 'bg-gray-100' : ''
      )}
      onClick={option.assignable ? onSelect : undefined}
    >
      <div className="flex-1">
        <div className="flex items-center gap-3">
          {
            <TimeshiftAvatar
              name={option.name}
              src={option.details?.avatarUrl}
              size={30}
              greyscale={!option.assignable}
            ></TimeshiftAvatar>
          }
          <div className="truncate flex-1 text-left">
            <div className="flex gap-1 text-sm items-center">
              <div className="text-md font-semibold">{option.name}</div>
              {option.details?.employeeId && (
                <>
                  <div>{`(${option.details.employeeId})`}</div>
                </>
              )}
            </div>
            <div className="flex gap-2 text-sm items-center">
              <div>{option.timeAvailable}</div>
              <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
              <div>{option.contract}</div>
            </div>
          </div>
          <Button className="p-1" title={`Opens team member page for ${option.name}`} onClick={onTeamMemberNavigate}>
            <MdOutlinePerson className="w-6 h-6" />
          </Button>
        </div>
        {option.employeeSummary && (
          <>
            <div className="flex-1 flex gap-2 items-center text-sm">
              <FiClock className="h-4 w-4" />
              <div>
                {strings.staffSelector.hoursWorked(secondsToDisplayHours(option.employeeSummary.compliance.actualTime))}
              </div>
            </div>
            <div className="flex-1 flex gap-2 items-center text-sm">
              <FiCalendar className="h-4 w-4" />
              <div>
                {strings.staffSelector.shiftsWorked(option.employeeSummary.compliance.attendanceSummary.length)}
              </div>
            </div>
            {option.majorIssues.map(issue => (
              <RosterIssue key={issue} issueText={issue} major />
            ))}
            {option.majorIssues.length === 0 &&
              option.minorIssues.map(issue => <RosterIssue key={issue} issueText={issue} />)}
          </>
        )}
      </div>
    </div>
  );
};
